import React from 'react';

/**
 * Manage a single form field.
 *
 * Reasoning: Our form field handling is very minimal. Additionally, our form field handling has
 * not been fully realized yet. In lieu of a full library, like react-hook-form, this hook should
 * suffice for now. Let's revisit if we need to build a form with more than three form fields.
 *
 * # Example
 *
 * ```
 * const initialValue = ''
 * const isValid = (value) => value !== ''
 * const {
 *   value,
 *   dirty,
 *   handleChange,
 *   handleBlur,
 *   errorMessage,
 *   setErrorMessage,
 *   isValid
 * } = useField(initialValue, isValid)
 *
 * React.useEffect(() => {
 *   if (isValid) {
 *     setErrorMessage('')
 *   }
 * }, [value, isValid, setErrorMessage])
 *
 * const handleSubmit = (e) => {
 *   e.preventDefault()
 *   if (!isValid) {
 *     setErrorMessage('Please describe your favorite color')
 *     return
 *   }
 * }
 *
 * <form submit={handleSubmit}>
 *   <div>
 *     <label htmlFor="color">Favorite Color</label>
 *     <input id="color" value={value} onChange={handleChange} onBlur={handleBlur} />
 *     {errorMessage && <span>{errorMessage}</span>}
 *   </div>
 *   <button type="submit" disabled={dirty && !isValid}>Submit</button>
 * </for>
 * ```
 *
 * # What about multiple fields?
 *
 * It's a bit wordy, but for a form with only two-or-three fields, it is not terrible.
 *
 * ```
 * const {
 *   value: firstNameValue,
 *   touched: firstNameTouched,
 *   handleChange: handleFirstNameChange,
 *   handleBlur: handleFirstNameBlur,
 *   errorMessage: firstNameErrorMessage,
 *   setErrorMessage: setFirstNameErrorMessage,
 *   isValid: isFirstNameValid
 * } = useField(initialFirstNameValue, isFirstNameValid)
 * ```
 */
export const useField = (initialValue, validate) => {
  const [value, setValue] = React.useState(initialValue);
  const [touched, setTouched] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');
  const handleChange = React.useCallback(e => {
    setValue(e.target.value);
    setDirty(e.target.value !== initialValue);
    if (!touched) setTouched(true);
  }, [touched, initialValue]);
  const handleBlur = React.useCallback(() => {
    if (!touched) setTouched(true);
  }, [touched]);
  const resetField = () => {
    setValue(initialValue);
    setTouched(false);
    setDirty(false);
    setErrorMessage('');
  };
  return {
    value,
    touched,
    dirty,
    handleChange,
    handleBlur,
    errorMessage,
    setErrorMessage,
    isValid: validate(value),
    resetField
  };
};